import type { PermissionGroupOut } from "@sagot/api-client";

/** Canonical action column order (SAGOT_BUILD_PROMPT §7.3 catalogue). */
const ACTION_ORDER = ["view", "create", "edit", "delete", "publish", "use", "reindex", "manage", "run", "reply", "takeover", "assign", "export", "resolve", "send", "invite"];

/** Actions present in the catalogue, in canonical order (unknown actions last, alphabetical). */
export function matrixColumns(groups: readonly PermissionGroupOut[]): string[] {
  const present = new Set(groups.flatMap((group) => group.permissions.map((p) => p.action)));
  const known = ACTION_ORDER.filter((action) => present.has(action));
  const extra = [...present].filter((action) => !ACTION_ORDER.includes(action)).sort();
  return [...known, ...extra];
}

export function permissionKey(group: PermissionGroupOut, action: string): string | undefined {
  return group.permissions.find((p) => p.action === action)?.key;
}

export function toggleKey(current: ReadonlySet<string>, key: string, on: boolean): Set<string> {
  const next = new Set(current);
  if (on) next.add(key);
  else next.delete(key);
  return next;
}

/** Row toggle: grants (or clears) every grantable permission of a module. */
export function toggleModule(current: ReadonlySet<string>, group: PermissionGroupOut, on: boolean, grantable: (key: string) => boolean): Set<string> {
  const next = new Set(current);
  for (const { key } of group.permissions) {
    if (!grantable(key)) continue;
    if (on) next.add(key);
    else next.delete(key);
  }
  return next;
}

export type RowState = boolean | "indeterminate";

export function rowState(current: ReadonlySet<string>, group: PermissionGroupOut): RowState {
  const held = group.permissions.filter((p) => current.has(p.key)).length;
  if (held === 0) return false;
  return held === group.permissions.length ? true : "indeterminate";
}

export function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((key) => b.has(key));
}
